import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { apiClient } from '../lib/api'
import {
  ChevronLeft,
  Users,
  UserPlus,
  CreditCard,
  Gift,
  Tag,
  TrendingUp,
  Settings,
  Plus,
  Edit,
  Trash2,
  Search,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Eye,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Award,
  Target,
  BarChart3,
  FileText,
  Shield,
  Clock,
  MessageSquare,
  Percent,
  Maximize
} from 'lucide-react'
import toast from 'react-hot-toast'

// Custom Components
import { ErrorBoundary } from '../components/ui/ErrorBoundary'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DataTable, Column } from '../components/ui/DataTable'

// Types
interface Customer {
  id: string;
  code: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  tax_number?: string;
  is_tax_exempt: boolean;
  discount_percentage: number;
  credit_limit: number;
  current_balance: number;
  customer_type: 'individual' | 'business';
  loyalty_points_balance: number;
  loyalty_enrollment_date?: string;
  loyalty_card_number?: string;
  customer_since_date: string;
  last_purchase_date?: string;
  total_lifetime_value: number;
  average_order_value: number;
  visit_count: number;
  vip_status: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CustomerGroup {
  id: string;
  name: string;
  discount_percentage: number;
  description?: string;
  is_active: boolean;
  member_count?: number;
  created_at: string;
}

interface CustomerDiscount {
  id: string;
  customer_id?: string;
  customer_group_id?: string;
  product_id?: string;
  category_id?: string;
  discount_percentage: number;
  is_active: boolean;
  start_date?: string;
  end_date?: string;
  created_at: string;
}

interface CreditPayment {
  id: string;
  customer_id: string;
  payment_amount: number;
  payment_type_id: string;
  payment_date: string;
  reference_number?: string;
  notes?: string;
  customer?: Customer;
  payment_type?: any;
}

interface CreditTransaction {
  id: string;
  customer_id: string;
  transaction_type: string;
  amount: number;
  order_id?: string;
  transaction_date: string;
  description?: string;
  customer?: Customer;
}

interface LoyaltyProgram {
  id: string;
  program_name: string;
  program_code: string;
  points_per_currency: number;
  currency_per_point: number;
  is_active: boolean;
  created_at: string;
}

interface LoyaltyTransaction {
  id: string;
  customer_id: string;
  loyalty_program_id: string;
  transaction_type: string;
  points: number;
  order_id?: string;
  transaction_date: string;
  customer?: Customer;
  loyalty_program?: LoyaltyProgram;
}

interface CustomerSegment {
  id: string;
  segment_name: string;
  segment_code: string;
  description?: string;
  filter_criteria?: any;
  is_active: boolean;
  member_count?: number;
  created_at: string;
}

interface GiftCard {
  id: string;
  card_number: string;
  initial_value: number;
  current_balance: number;
  customer_id?: string;
  status: string;
  expiry_date?: string;
  customer?: Customer;
}

interface CustomerFeedback {
  id: string;
  customer_id: string;
  order_id?: string;
  feedback_type: string;
  rating?: number;
  comment?: string;
  feedback_date: string;
  is_resolved: boolean;
  customer?: Customer;
}

interface CustomerAnalytics {
  totalCustomers?: number;
  activeCustomers?: number;
  vipCustomers?: number;
  totalLifetimeValue?: number;
  averageOrderValue?: number;
  totalLoyaltyPoints?: number;
  totalCreditBalance?: number;
  segmentBreakdown?: any[];
}

type TabType =
  | 'customers'
  | 'groups'
  | 'discounts'
  | 'credit'
  | 'loyalty'
  | 'segments'
  | 'gift-cards'
  | 'feedback'
  | 'analytics';

type CustomerSubTab = 'all' | 'individual' | 'business' | 'vip' | 'inactive';
type CreditSubTab = 'balances' | 'payments' | 'transactions' | 'aging' | 'statements';
type LoyaltySubTab = 'programs' | 'transactions' | 'rewards' | 'tiers';
type SegmentSubTab = 'segments' | 'members' | 'campaigns';
type FeedbackSubTab = 'all' | 'pending' | 'resolved';

const ITEMS_PER_PAGE = 50;
const DEBOUNCE_DELAY = 300;

export default function CustomersPage() {
  useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('customers');
  const [activeSubTab, setActiveSubTab] = useState<any>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<any>({});

  // Customers Data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [customerDiscounts, setCustomerDiscounts] = useState<CustomerDiscount[]>([]);

  // Credit Data
  const [creditPayments, setCreditPayments] = useState<CreditPayment[]>([]);
  const [creditTransactions, setCreditTransactions] = useState<CreditTransaction[]>([]);
  const [creditBalances, setCreditBalances] = useState<any[]>([]);
  const [agingBuckets, setAgingBuckets] = useState<any[]>([]);

  // Loyalty Data
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<LoyaltyProgram[]>([]);
  const [loyaltyTransactions, setLoyaltyTransactions] = useState<LoyaltyTransaction[]>([]);
  const [loyaltyRewards, setLoyaltyRewards] = useState<any[]>([]);
  const [loyaltyTiers, setLoyaltyTiers] = useState<any[]>([]);

  // Segments Data
  const [customerSegments, setCustomerSegments] = useState<CustomerSegment[]>([]);
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [customerFeedback, setCustomerFeedback] = useState<CustomerFeedback[]>([]);

  // Analytics Data
  const [analytics, setAnalytics] = useState<CustomerAnalytics>({});

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOptions] = useState<any>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        fetchData();
      }
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data on tab/subtab change
  useEffect(() => {
    fetchData();
  }, [activeTab, activeSubTab, currentPage]);

  /**
   * Main data fetching orchestrator with error handling
   */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      switch (activeTab) {
        case 'customers':
          await fetchCustomersData();
          break;
        case 'groups':
          await fetchGroupsData();
          break;
        case 'discounts':
          await fetchDiscountsData();
          break;
        case 'credit':
          await fetchCreditData();
          break;
        case 'loyalty':
          await fetchLoyaltyData();
          break;
        case 'segments':
          await fetchSegmentsData();
          break;
        case 'gift-cards':
          await fetchGiftCardsData();
          break;
        case 'feedback':
          await fetchFeedbackData();
          break;
        case 'analytics':
          await fetchAnalyticsData();
          break;
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeSubTab, currentPage, searchTerm, filterOptions]);

  const fetchCustomersData = async () => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: ITEMS_PER_PAGE.toString(),
      search: searchTerm,
      ...filterOptions
    });

    if (activeSubTab !== 'all') {
      if (activeSubTab === 'inactive') {
        params.append('is_active', 'false');
      } else if (activeSubTab === 'vip') {
        params.append('vip_status', 'true');
      } else {
        params.append('customer_type', activeSubTab);
      }
    }

    const response = await apiClient.get(`/api/customers?${params}`);
    const rawData = response.data.data ?? response.data;
    setCustomers(Array.isArray(rawData) ? rawData : []);
    setTotalCount(response.data.total ?? (Array.isArray(rawData) ? rawData.length : 0));
  };

  const fetchGroupsData = async () => {
    const response = await apiClient.get('/api/customer-groups');
    const data = response.data?.data ?? response.data;
    setCustomerGroups(Array.isArray(data) ? data : []);
  };

  const fetchDiscountsData = async () => {
    const response = await apiClient.get('/api/customer-discounts');
    const data = response.data?.data ?? response.data;
    setCustomerDiscounts(Array.isArray(data) ? data : []);
  };

  const fetchCreditData = async () => {
    if (activeSubTab === 'balances') {
      const response = await apiClient.get('/api/customers/credit-balances');
      const data = response.data?.data ?? response.data;
      setCreditBalances(Array.isArray(data) ? data : []);
    } else if (activeSubTab === 'payments') {
      const response = await apiClient.get('/api/credit-payments');
      const data = response.data?.data ?? response.data;
      setCreditPayments(Array.isArray(data) ? data : []);
    } else if (activeSubTab === 'transactions') {
      const response = await apiClient.get('/api/credit-transactions');
      const data = response.data?.data ?? response.data;
      setCreditTransactions(Array.isArray(data) ? data : []);
    } else if (activeSubTab === 'aging') {
      const response = await apiClient.get('/api/customers/credit-aging');
      const data = response.data?.data ?? response.data;
      setAgingBuckets(Array.isArray(data) ? data : []);
    }
  };

  const fetchLoyaltyData = async () => {
    if (activeSubTab === 'programs') {
      const response = await apiClient.get('/api/loyalty/programs');
      const data = response.data?.data ?? response.data;
      setLoyaltyPrograms(Array.isArray(data) ? data : []);
    } else if (activeSubTab === 'transactions') {
      const response = await apiClient.get('/api/loyalty/transactions');
      const data = response.data?.data ?? response.data;
      setLoyaltyTransactions(Array.isArray(data) ? data : []);
    } else if (activeSubTab === 'rewards') {
      const response = await apiClient.get('/api/loyalty/rewards');
      const data = response.data?.data ?? response.data;
      setLoyaltyRewards(Array.isArray(data) ? data : []);
    } else if (activeSubTab === 'tiers') {
      const response = await apiClient.get('/api/loyalty/tiers');
      const data = response.data?.data ?? response.data;
      setLoyaltyTiers(Array.isArray(data) ? data : []);
    }
  };

  const fetchSegmentsData = async () => {
    const response = await apiClient.get('/api/customer-segments');
    const data = response.data?.data ?? response.data;
    setCustomerSegments(Array.isArray(data) ? data : []);
  };

  const fetchGiftCardsData = async () => {
    const response = await apiClient.get('/api/gift-cards');
    const data = response.data?.data ?? response.data;
    setGiftCards(Array.isArray(data) ? data : []);
  };

  const fetchFeedbackData = async () => {
    const params = new URLSearchParams();
    if (activeSubTab === 'pending') {
      params.append('is_resolved', 'false');
    } else if (activeSubTab === 'resolved') {
      params.append('is_resolved', 'true');
    }
    const response = await apiClient.get(`/api/customer-feedback?${params}`);
    const data = response.data?.data ?? response.data;
    setCustomerFeedback(Array.isArray(data) ? data : []);
  };

  const fetchAnalyticsData = async () => {
    const response = await apiClient.get('/api/customers/analytics');
    const data = response.data?.data ?? response.data;
    setAnalytics(data && typeof data === 'object' && !Array.isArray(data) ? data : {});
  };

  const handleMaximize = () => {
    setIsFullscreen(!isFullscreen);
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const openConfirmDialog = (props: any) => {
    setConfirmDialogProps(props);
    setShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setConfirmDialogProps({});
  };

  const confirmAction = () => {
    if (confirmDialogProps.onConfirm) {
      confirmDialogProps.onConfirm();
    }
    closeConfirmDialog();
  };

  /**
   * Customer Columns
   */
  const customerColumns: Column<Customer>[] = useMemo(() => [
    {
      key: 'code',
      header: 'Code',
      accessor: (row) => row.code,
      sortable: true,
      filterable: true,
      width: '100px'
    },
    {
      key: 'name',
      header: 'Customer',
      accessor: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.company_name,
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <div className="text-white font-medium">{value}</div>
          {row.email && <div className="text-xs text-gray-500">{row.email}</div>}
        </div>
      )
    },
    {
      key: 'type',
      header: 'Type',
      accessor: (row) => row.customer_type,
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'business' ? 'bg-blue-900 text-blue-200' : 'bg-gray-700 text-gray-300'
        }`}>
          {value === 'business' ? 'Business' : 'Individual'}
        </span>
      )
    },
    {
      key: 'phone',
      header: 'Contact',
      accessor: (row) => row.phone || row.mobile,
      render: (value) => value || '-'
    },
    {
      key: 'credit_limit',
      header: 'Credit Limit',
      accessor: (row) => row.credit_limit,
      align: 'right',
      render: (value) => value ? `$${parseFloat(value.toString()).toFixed(2)}` : '-',
      sortable: true
    },
    {
      key: 'current_balance',
      header: 'Balance',
      accessor: (row) => row.current_balance,
      align: 'right',
      render: (value, row) => (
        <span className={`font-semibold ${
          parseFloat(value?.toString() || '0') > parseFloat(row.credit_limit?.toString() || '0')
            ? 'text-red-400'
            : 'text-green-400'
        }`}>
          ${parseFloat(value?.toString() || '0').toFixed(2)}
        </span>
      ),
      sortable: true
    },
    {
      key: 'loyalty_points_balance',
      header: 'Points',
      accessor: (row) => row.loyalty_points_balance || 0,
      align: 'right',
      className: 'text-yellow-400'
    },
    {
      key: 'is_active',
      header: 'Status',
      accessor: (row) => row.is_active,
      align: 'center',
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: true
    }
  ], []);

  const renderContent = () => {
    switch (activeTab) {
      case 'customers':
        return renderCustomersContent();
      case 'groups':
        return renderGroupsContent();
      case 'discounts':
        return renderDiscountsContent();
      case 'credit':
        return renderCreditContent();
      case 'loyalty':
        return renderLoyaltyContent();
      case 'segments':
        return renderSegmentsContent();
      case 'gift-cards':
        return renderGiftCardsContent();
      case 'feedback':
        return renderFeedbackContent();
      case 'analytics':
        return renderAnalyticsContent();
      default:
        return null;
    }
  };

  const renderCustomersContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Customers', icon: Users },
      { id: 'individual', label: 'Individual', icon: Users },
      { id: 'business', label: 'Business', icon: Users },
      { id: 'vip', label: 'VIP', icon: Award },
      { id: 'inactive', label: 'Inactive', icon: AlertTriangle }
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveSubTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => toast.success('Export started')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Download size={18} />
              Export
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
              <Plus size={18} />
              New Customer
            </button>
          </div>
        </div>

        <DataTable
          data={customers}
          columns={customerColumns}
          loading={loading}
          error={error}
          pageSize={ITEMS_PER_PAGE}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          rowKey={(row) => row.id}
          actions={(row) => (
            <div className="flex items-center justify-center gap-2">
              <button className="p-1 hover:bg-gray-700 rounded" title="View">
                <Eye size={16} className="text-blue-400" />
              </button>
              <button className="p-1 hover:bg-gray-700 rounded" title="Edit">
                <Edit size={16} className="text-yellow-400" />
              </button>
              <button className="p-1 hover:bg-gray-700 rounded" title="Delete">
                <Trash2 size={16} className="text-red-400" />
              </button>
            </div>
          )}
          emptyState={
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <Users size={48} className="mx-auto text-gray-600 mb-4" />
              <div className="text-gray-400 text-lg mb-2">No customers found</div>
              <div className="text-gray-500 text-sm mb-4">Get started by adding your first customer</div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Plus size={18} className="inline mr-2" />
                Add Customer
              </button>
            </div>
          }
        />
      </div>
    );
  };

  const renderGroupsContent = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Customer Groups</h3>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Plus size={18} />
          New Group
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {customerGroups.map(group => (
          <div key={group.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h4 className="text-white font-medium mb-2">{group.name}</h4>
            <p className="text-sm text-gray-400 mb-4">{group.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Discount: {group.discount_percentage}%</span>
              <span className={`px-2 py-1 text-xs rounded-full ${group.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'}`}>
                {group.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDiscountsContent = () => (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <h3 className="text-white font-semibold">Customer Discounts</h3>
        <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          <Plus size={16} className="inline mr-2" />
          New Discount Rule
        </button>
      </div>
      <table className="w-full">
        <thead className="bg-gray-700">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Customer/Group</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Product/Category</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Discount %</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Valid Period</th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {customerDiscounts.map(discount => (
            <tr key={discount.id} className="hover:bg-gray-750">
              <td className="px-4 py-3 text-sm text-white">-</td>
              <td className="px-4 py-3 text-sm text-gray-400">-</td>
              <td className="px-4 py-3 text-sm text-right text-green-400">{discount.discount_percentage}%</td>
              <td className="px-4 py-3 text-sm text-gray-400">-</td>
              <td className="px-4 py-3 text-center">
                <span className={`px-2 py-1 text-xs rounded-full ${discount.is_active ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'}`}>
                  {discount.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderCreditContent = () => {
    const subTabs = [
      { id: 'balances', label: 'Credit Balances', icon: DollarSign },
      { id: 'payments', label: 'Payments', icon: CreditCard },
      { id: 'transactions', label: 'Transactions', icon: FileText },
      { id: 'aging', label: 'Aging Analysis', icon: Clock },
      { id: 'statements', label: 'Statements', icon: FileText }
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeSubTab === 'payments' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Customer</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {creditPayments.map(payment => (
                  <tr key={payment.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-white">{payment.customer?.first_name}</td>
                    <td className="px-4 py-3 text-sm text-right text-green-400">
                      ${parseFloat(payment.payment_amount.toString()).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">{payment.reference_number || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeSubTab === 'transactions' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {creditTransactions.map(transaction => (
                  <tr key={transaction.id} className="hover:bg-gray-750">
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {new Date(transaction.transaction_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-white">{transaction.customer?.first_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{transaction.transaction_type}</td>
                    <td className="px-4 py-3 text-sm text-right text-white">
                      ${parseFloat(transaction.amount.toString()).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeSubTab === 'balances' && (
          <div className="text-gray-400">Credit balances content</div>
        )}
        {activeSubTab === 'aging' && (
          <div className="text-gray-400">Aging analysis content</div>
        )}
        {activeSubTab === 'statements' && (
          <div className="text-gray-400">Statements content</div>
        )}
      </div>
    );
  };

  const renderLoyaltyContent = () => {
    const subTabs = [
      { id: 'programs', label: 'Programs', icon: Award },
      { id: 'transactions', label: 'Transactions', icon: TrendingUp },
      { id: 'rewards', label: 'Rewards', icon: Gift },
      { id: 'tiers', label: 'Tiers', icon: Target }
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="text-gray-400">Loyalty content for {activeSubTab}</div>
      </div>
    );
  };

  const renderSegmentsContent = () => (
    <div className="space-y-4">
      <div className="text-gray-400">Customer segments content</div>
    </div>
  );

  const renderGiftCardsContent = () => (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Card Number</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-300">Customer</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Initial Value</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-300">Current Balance</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {giftCards.map(card => (
              <tr key={card.id} className="hover:bg-gray-750">
                <td className="px-4 py-3 text-sm text-white font-mono">{card.card_number}</td>
                <td className="px-4 py-3 text-sm text-gray-400">{card.customer?.first_name || '-'}</td>
                <td className="px-4 py-3 text-sm text-right text-gray-300">
                  ${parseFloat(card.initial_value.toString()).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-green-400">
                  ${parseFloat(card.current_balance.toString()).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    card.status === 'Active' ? 'bg-green-900 text-green-200' : 'bg-gray-700 text-gray-400'
                  }`}>
                    {card.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderFeedbackContent = () => {
    const subTabs = [
      { id: 'all', label: 'All Feedback', icon: MessageSquare },
      { id: 'pending', label: 'Pending', icon: Clock },
      { id: 'resolved', label: 'Resolved', icon: CheckCircle }
    ];

    return (
      <div className="space-y-4">
        <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
          {subTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeSubTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="space-y-3">
          {customerFeedback.map(feedback => (
            <div key={feedback.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-white font-medium">{feedback.customer?.first_name}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(feedback.feedback_date).toLocaleDateString()}
                  </div>
                </div>
                {feedback.rating && (
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className={i < feedback.rating! ? 'text-yellow-400' : 'text-gray-600'}>
                        ★
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-300 mb-2">{feedback.comment}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{feedback.feedback_type}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  feedback.is_resolved ? 'bg-green-900 text-green-200' : 'bg-yellow-900 text-yellow-200'
                }`}>
                  {feedback.is_resolved ? 'Resolved' : 'Pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAnalyticsContent = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Total Customers</span>
            <Users className="text-blue-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.totalCustomers || 0}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Lifetime Value</span>
            <DollarSign className="text-green-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">
            ${(analytics.totalLifetimeValue || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Loyalty Points</span>
            <Award className="text-yellow-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">{analytics.totalLoyaltyPoints || 0}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Credit Balance</span>
            <CreditCard className="text-orange-400" size={24} />
          </div>
          <div className="text-2xl font-bold text-white">
            ${(analytics.totalCreditBalance || 0).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-900">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-3 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-4">
            <Link to="/app" className="text-gray-400 hover:text-white transition-colors">
              <ChevronLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <Users size={22} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Customer Relationship Management</h2>
                <div className="text-sm text-gray-500">Manage customer profiles, loyalty, and credit</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="p-2 hover:bg-gray-700 rounded"
              onClick={fetchData}
              title="Refresh"
            >
              <RefreshCw size={18} className="text-gray-400" />
            </button>
            <button onClick={handleMaximize} className="p-2 hover:bg-gray-700 rounded" title="Fullscreen">
              <Maximize size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="flex gap-1 px-6 overflow-x-auto">
            {[
              { id: 'customers', label: 'Customers', icon: Users },
              { id: 'groups', label: 'Groups', icon: Users },
              { id: 'discounts', label: 'Discounts', icon: Tag },
              { id: 'credit', label: 'Credit', icon: CreditCard },
              { id: 'loyalty', label: 'Loyalty', icon: Award },
              { id: 'segments', label: 'Segments', icon: Target },
              { id: 'gift-cards', label: 'Gift Cards', icon: Gift },
              { id: 'feedback', label: 'Feedback', icon: MessageSquare },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as TabType);
                    setActiveSubTab(
                      tab.id === 'customers' ? 'all' :
                      tab.id === 'credit' ? 'balances' :
                      tab.id === 'loyalty' ? 'programs' :
                      tab.id === 'segments' ? 'segments' :
                      tab.id === 'feedback' ? 'all' : 'all'
                    );
                    setCurrentPage(1);
                  }}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400 bg-gray-750'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-750'
                  }`}
                >
                  <Icon size={16} />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={closeConfirmDialog}
        title={confirmDialogProps.title}
        message={confirmDialogProps.message}
        onConfirm={confirmAction}
      />
    </ErrorBoundary>
  );
}